/**
 * Table column definitions for CWE neutralization entries.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { GENERATOR_COLOR, GRAY, WARN } from "../../design-system/tokens.js";
import { Pill } from "../Pill.js";
import { theoremPresentation } from "../proofViewShared.js";
import type { CweNeutralizationEntry } from "./types.js";

export function createCweEntryColumns(): readonly ColumnDef<CweNeutralizationEntry>[] {
  return [
    {
      id: "rank",
      header: "Rank",
      accessorKey: "rank",
      cell: ({ row }): React.JSX.Element => (
        <span style={{ fontWeight: 700, color: GRAY[700] }}>{row.original.rank}</span>
      ),
    },
    {
      id: "cwe_id",
      header: "CWE",
      accessorKey: "cwe_id",
      cell: ({ row }): React.JSX.Element => (
        <span style={{ fontWeight: 700, color: GENERATOR_COLOR.G2.primary }}>
          CWE-{row.original.cwe_id}
        </span>
      ),
    },
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: ({ row }): React.JSX.Element => (
        <>
          <div style={{ fontWeight: 600 }}>{row.original.name}</div>
          {row.original.reclassified === true && (
            <div style={{ color: WARN[700], fontSize: "0.75rem", marginTop: "0.2rem" }}>
              Reclassified into Regime 1
            </div>
          )}
        </>
      ),
    },
    {
      id: "generators",
      header: "Generators",
      cell: ({ row }): React.JSX.Element => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {row.original.generators.map((generator) => (
            <Pill
              key={`${row.original.cwe_id}-${generator}`}
              color={GENERATOR_COLOR[generator].primary}
              bg={`${GENERATOR_COLOR[generator].primary}12`}
              border={`${GENERATOR_COLOR[generator].primary}33`}
            >
              {generator}
            </Pill>
          ))}
        </div>
      ),
    },
    {
      id: "theorems",
      header: "Theorems",
      cell: ({ row }): React.JSX.Element => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {row.original.theorems.map((theoremCode) => {
            const theorem = theoremPresentation(theoremCode);
            return (
              <Pill
                key={`${row.original.cwe_id}-${theoremCode}`}
                color={theorem.color}
                bg={theorem.bg}
                border={`${theorem.color}33`}
                title={theorem.title}
              >
                {theorem.label}
              </Pill>
            );
          })}
        </div>
      ),
    },
    {
      id: "pattern",
      header: "Pattern",
      accessorKey: "pattern",
      cell: ({ row }): React.JSX.Element => (
        <span style={{ color: GRAY[700] }}>
          {row.original.pattern}
          {row.original.owasp !== undefined && (
            <div style={{ color: GRAY[500], fontSize: "0.75rem", marginTop: "0.2rem" }}>
              OWASP {row.original.owasp}
            </div>
          )}
        </span>
      ),
    },
  ];
}
